import React, { useEffect, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import supabase from "../supabaseClient";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import logo from "../assets/RCS-Logo-Blue.png"; // Import the logo image
import ReactPaginate from "react-paginate";
import { isCompletedShipmentStatus } from "../utils/shipmentProgress";
import {
  DASHBOARD_MONTH_OPTIONS,
  matchesDashboardDateFilter,
} from "../utils/dashboardDateFilter";

const InactiveShipment = ({ selectedYear = "all", selectedMonth = "all", selectedShipmentType = "all" }) => {
  const [loading, setLoading] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(6);
  const selectedMonthLabel =
    DASHBOARD_MONTH_OPTIONS.find((month) => month.value === selectedMonth)?.label || "Selected Month";

  const filteredShipments = shipments.filter((shipment) => {
    return (
      (selectedShipmentType === "all" || shipment.shipment_type === selectedShipmentType) &&
      matchesDashboardDateFilter(
      shipment.job_order,
      shipment.date,
      selectedYear,
      selectedMonth
      )
    );
  });

  // Calculate the data to display
  const offset = currentPage * rowsPerPage;
  const currentData = filteredShipments.slice(offset, offset + rowsPerPage);
  const pageCount = Math.ceil(filteredShipments.length / rowsPerPage);

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  useEffect(() => {
    if (selectedShipment) {
      downloadShipmentPDF(selectedShipment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShipment]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedYear, selectedMonth, selectedShipmentType]);

  const downloadShipmentPDF = () => {
    if (selectedShipment) {
      const pdf = new jsPDF({ orientation: "landscape" });
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Add the logo
      const logoX = 10;
      const logoY = 10;
      const logoWidth = 40;
      const logoHeight = 20;
      pdf.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

      // Title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text("Shipment Report", pageWidth / 2, logoY + 15, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.text(`BL #: ${selectedShipment.tracking_no || "—"}`, pageWidth / 2, logoY + 22, {
        align: "center",
      });

      let currentY = logoY + logoHeight + 32;
      const filterValidFields = (data) =>
        data.filter(
          (row) =>
            row.details !== null &&
            row.details !== undefined &&
            row.details !== ""
        );
      const formatDateTime = (date) =>
        date
          ? new Date(date).toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "Asia/Manila",
            })
          : "Date not set";

      // Section: Shipper Details
      pdf.setFontSize(16);
      pdf.text("Shipper Details", 10, currentY);
      currentY += 8;

      const shipperDetails = filterValidFields([
        { description: "Shipper Name", details: selectedShipment.shipper_name },
        { description: "Shipper Unit", details: selectedShipment.shipper_unit },
        {
          description: "Shipper Email",
          details: selectedShipment.shipper_email,
        },
        { description: "Shipper City", details: selectedShipment.shipper_city },
        {
          description: "Shipper Street",
          details: selectedShipment.shipper_street,
        },
        {
          description: "Shipper Country",
          details: selectedShipment.shipper_country,
        },
        {
          description: "Shipper Phone",
          details: selectedShipment.shipper_phone,
        },
        {
          description: "Shipper Building",
          details: selectedShipment.shipper_building,
        },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: shipperDetails.map((row) => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Receiver Details
      pdf.setFontSize(16);
      pdf.text("Receiver Details", 10, currentY);
      currentY += 8;

      const receiverDetails = filterValidFields([
        {
          description: "Receiver Name",
          details: selectedShipment.receiver_name,
        },
        {
          description: "Receiver Unit",
          details: selectedShipment.receiver_unit,
        },
        {
          description: "Receiver Email",
          details: selectedShipment.receiver_email,
        },
        {
          description: "Receiver City",
          details: selectedShipment.receiver_city,
        },
        {
          description: "Receiver Street",
          details: selectedShipment.receiver_street,
        },
        {
          description: "Receiver Country",
          details: selectedShipment.receiver_country,
        },
        {
          description: "Receiver Phone",
          details: selectedShipment.receiver_phone,
        },
        {
          description: "Receiver Building",
          details: selectedShipment.receiver_building,
        },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: receiverDetails.map((row) => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Shipment Details
      pdf.setFontSize(16);
      pdf.text("Shipment Details", 10, currentY);
      currentY += 8;

      const shipmentDetails = filterValidFields([
        { description: "Shipment ID", details: selectedShipment.shipment_id },
        { description: "Job Order", details: selectedShipment.job_order },
        { description: "Type", details: selectedShipment.shipment_type },
        { description: "Origin", details: selectedShipment.shipper_city },
        { description: "Destination", details: selectedShipment.receiver_city },
        { description: "Status", details: selectedShipment.status },
        { description: "Created By", details: selectedShipment.created_by },
        {
          description: "Description",
          details: selectedShipment.shipment_description,
        },
        {
          description: "Shipment Code",
          details: selectedShipment.shipment_code,
        },

        // Additional fields from shipmentDetails
        {
          description: "Shipment Description",
          details: selectedShipment.shipment_description || null,
        },
        {
          description: "Shipment Type",
          details: selectedShipment.shipment_type || null,
        },
        {
          description: "Shipment Instructions",
          details: selectedShipment.shipment_instructions || null,
        },
        {
          description: "Shipment Mode",
          details: selectedShipment.shipment_mode || null,
        },
        {
          description: "Shipment Transport",
          details: selectedShipment.shipment_transport || null,
        },

        // Sea-specific fields
        {
          description: "Shipment Terms",
          details: selectedShipment.shipment_terms || null,
        },
        {
          description: "Shipment Packages",
          details: selectedShipment.shipment_packages || null,
        },
        {
          description: "Shipment Volume",
          details: selectedShipment.shipment_volume || null,
        },

        // Sea FCL-specific fields
        {
          description: "Shipment Containers",
          details:
            Array.isArray(selectedShipment.shipment_containers) &&
            selectedShipment.shipment_containers.length > 0
              ? selectedShipment.shipment_containers.join(", ")
              : null,
        },
        {
          description: "Shipment Weight",
          details: selectedShipment.shipment_weight || null,
        },

        // Air-specific fields
        {
          description: "Shipment Dimensions",
          details:
            Array.isArray(selectedShipment.shipment_dimensions) &&
            selectedShipment.shipment_dimensions.length > 0
              ? selectedShipment.shipment_dimensions.join(", ")
              : null,
        },
        {
          description: "Shipment Chargeable Weight",
          details: selectedShipment.shipment_chargeableweight || null,
        },

        // Sea LCL and Air-specific fields
        {
          description: "Shipment Gross Weight",
          details: selectedShipment.shipment_grossweight || null,
        },
        {
          description: "Shipment Bound",
          details: selectedShipment.shipment_bound || null,
        },
        {
          description: "Commodity",
          details: selectedShipment.commodity || null,
        },
        {
          description: "Number of Pieces",
          details: selectedShipment.number_pcs || null,
        },
        { description: "CBM", details: selectedShipment.cbm || null },
        { description: "Origin", details: selectedShipment.origin || null },
        {
          description: "Destination",
          details: selectedShipment.destination || null,
        },
        { description: "Flight", details: selectedShipment.flight || null },
        {
          description: "Truck Type",
          details: selectedShipment.truck_type || null,
        },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: shipmentDetails.map((row) => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Brokerage Remarks (if applicable)
      if (
        selectedShipment.shipment_type === "BROKERAGE" &&
        selectedShipment.brokerage_remarks?.length > 0
      ) {
        pdf.setFontSize(16);
        pdf.text("Brokerage Remarks", 10, currentY);
        currentY += 8;

        const sortedBrokerageRemarks = selectedShipment.brokerage_remarks.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        const brokerageTableData = sortedBrokerageRemarks.map((remark) => ({
          timestamp: formatDateTime(remark.timestamp),
          title: remark.brokerage_title,
          input: remark.brokerage_input,
        }));

        pdf.autoTable({
          startY: currentY,
          head: [
            ["Date and Time", "Brokerage Remarks Title", "Brokerage Remarks"],
          ],
          body: brokerageTableData.map((row) => [
            row.timestamp,
            row.title,
            row.input,
          ]),
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
          bodyStyles: { fillColor: [240, 240, 240] },
        });

        currentY = pdf.lastAutoTable.finalY + 10;
      }

      // Section: Remarks (if applicable)
      if (selectedShipment.remarks && selectedShipment.remarks.length > 0) {
        pdf.setFontSize(16);
        pdf.text("Remarks", 10, currentY);
        currentY += 8;

        const sortedRemarks = selectedShipment.remarks.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        const remarksTableData = sortedRemarks.map((remark) => {
          const receivedDate = formatDateTime(remark.receivedDate);
          const transitDate = formatDateTime(remark.transitDate);
          const aad = formatDateTime(remark.aad);
          const atd = formatDateTime(remark.atd);
          const eta = formatDateTime(selectedShipment.eta);
          const etd = formatDateTime(selectedShipment.etd);

          // Determine shipment status
          const orderPlaced = remark.status === "Order Placed";
          const departureAtOrigin = remark.status === "Departure at Origin";
          const arrivalAtDestination =
            remark.status === "Arrival at Destination";
          const inTransit = remark.status === "In Transit";

          // Construct remarks string dynamically
          let fullRemark = remark.remark
            ? `${remark.remark}\n`
            : "No Remarks Available\n";

          if (orderPlaced)
            fullRemark += `Shipment Nomination Date: ${receivedDate}\n`;
          if (orderPlaced || departureAtOrigin) fullRemark += `ETD: ${etd}\n`;
          if (departureAtOrigin) fullRemark += `ATD: ${atd}\n`;
          if (orderPlaced || arrivalAtDestination)
            fullRemark += `ETA: ${eta}\n`;
          if (arrivalAtDestination) fullRemark += `ATA: ${aad}\n`;

          // ✅ Add Transit Type & Transit Date for "In Transit" status
          if (inTransit) {
            fullRemark += `Transit Type: ${
              remark.transitType || "Date not set"
            }\n`;
            fullRemark += `Transit Date: ${transitDate}\n`;
            fullRemark += `ETD: ${etd}\n`;
            fullRemark += `ETA: ${eta}\n`;
          }

          // Debugging: Log the final remark
          console.log("Full Remark for", remark.status, ":", fullRemark);

          return {
            dateTime: formatDateTime(remark.date),
            status: remark.status || "No Status Available",
            remarks: fullRemark.trim(), // Trim to remove extra newlines
          };
        });

        pdf.autoTable({
          startY: currentY,
          head: [["Date and Time", "Status", "Remarks"]],
          body: remarksTableData.map((row) => [
            row.dateTime,
            row.status,
            row.remarks,
          ]),
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
          bodyStyles: { fillColor: [240, 240, 240] },
        });
      }
      // Save the PDF
      pdf.save(`shipment-${selectedShipment.tracking_no}.pdf`);
    } else {
      console.error("No shipment selected for download.");
    }
  };

  useEffect(() => {
    fetchStatusShipments();

    // ✅ Real-time subscription
    const channel = supabase
      .channel("shipments")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shipments" },
        (payload) => {
          console.log("🔄 Real-time shipment update received:", payload);
          fetchStatusShipments(); // Refresh shipments on change
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe(); // ✅ Use the same channel instance for cleanup
    };
  }, []); // Removed fetchStatusShipments from dependencies to prevent unnecessary re-renders

  const fetchStatusShipments = async () => {
    setLoading(true);

    // Calculate 24 hours ago in ISO format
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    try {
      const { data, error } = await supabase
        .from("shipments")
        .select("*")
        .not("status_updated_at", "is", null)
        .lt("status_updated_at", oneDayAgo) // fetch only shipments last updated over 24 hrs ago
        .order("status_updated_at", { ascending: false });

      if (error) throw error;

      // Filter out completed shipments
      const filtered = (data || []).filter(
        (s) => !isCompletedShipmentStatus(s.status?.trim())
      );

      setShipments(filtered);
    } catch (err) {
      console.error("Error fetching inactive shipments:", err);
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    const intervals = [
      { label: "year", seconds: 31536000 },
      { label: "month", seconds: 2592000 },
      { label: "week", seconds: 604800 },
      { label: "day", seconds: 86400 },
      { label: "hour", seconds: 3600 },
      { label: "minute", seconds: 60 },
      { label: "second", seconds: 1 },
    ];

    for (const interval of intervals) {
      const count = Math.floor(seconds / interval.seconds);
      if (count > 0) {
        return `${count} ${interval.label}${count !== 1 ? "s" : ""} ago`;
      }
    }

    return "just now";
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'order placed':
        return 'bg-blue/10 text-blue border-blue/30';
      case 'in warehouse':
        return 'bg-yellow/10 text-blue border-yellow/30';
      case 'departure at origin':
        return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'in transit':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'arrival at destination':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'custom clearance':
        return 'bg-indigo-100 text-indigo-700 border-indigo-300';
      case 'turnover':
        return 'bg-teal-100 text-teal-700 border-teal-300';
      case 'delivered':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="w-full">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
            <div className="w-12 h-12 border-4 border-transparent border-t-yellow rounded-full animate-spin absolute inset-0 animate-ping"></div>
          </div>
          <div className="text-blue font-medium">Loading inactive shipments...</div>
        </div>
      ) : (
        <div className="w-full space-y-6">
          {currentData.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table className="w-full">
                    <Thead>
                      <Tr className="bg-blue text-white">
                        <Th className="px-6 py-4 text-left font-semibold text-sm tracking-wide">
                          <div className="flex items-center gap-2">
                            Tracking Number
                          </div>
                        </Th>
                        <Th className="px-6 py-4 text-left font-semibold text-sm tracking-wide">
                          <div className="flex items-center gap-2">
                            Assigned User
                          </div>
                        </Th>
                        <Th className="px-6 py-4 text-left font-semibold text-sm tracking-wide">
                          <div className="flex items-center gap-2">
                            Status
                          </div>
                        </Th>
                        <Th className="px-6 py-4 text-left font-semibold text-sm tracking-wide">
                          <div className="flex items-center gap-2">
                            Last Activity
                          </div>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {currentData.map((shipment, index) => (
                        <Tr
                          key={index}
                          className="border-b border-gray-100/50"
                        >
                          <Td
                            className="px-6 py-4 cursor-pointer"
                            onClick={() => setSelectedShipment(shipment)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue rounded-full flex items-center justify-center shadow-sm">
                                <i className="fas fa-download text-white text-xs"></i>
                              </div>
                              <div>
                                <div className="font-semibold text-blue">
                                  {shipment.tracking_no}
                                </div>
                                <div className="text-xs text-grey">Click to download PDF</div>
                              </div>
                            </div>
                          </Td>
                          <Td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue/20 to-blue/30 rounded-full flex items-center justify-center">
                                <i className="fas fa-user text-blue text-xs"></i>
                              </div>
                              <span className="font-medium text-grey">
                                {shipment.current_user_name || 'Unassigned'}
                              </span>
                            </div>
                          </Td>
                          <Td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(shipment.status)}`}>
                              <div className="w-1.5 h-1.5 bg-current rounded-full mr-2 animate-pulse"></div>
                              {shipment.status}
                            </span>
                          </Td>
                          <Td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-medium text-grey">
                                {timeAgo(shipment.status_updated_at)}
                              </span>
                              <span className="text-xs text-grey/60">
                                {new Date(shipment.status_updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {currentData.map((shipment, index) => (
                  <div
                    key={index}
                    className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-100"
                  >
                    {/* Header Row */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-blue rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                        <i className="fas fa-shipping-fast text-white text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-bold text-blue text-lg cursor-pointer truncate"
                          onClick={() => setSelectedShipment(shipment)}
                        >
                          {shipment.tracking_no}
                        </h3>
                        <p className="text-sm text-grey">Tap to download PDF</p>
                      </div>
                    </div>

                    {/* Status Badge Row */}
                    <div className="mb-4">
                      <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(shipment.status)}`}>
                        <div className="w-2 h-2 bg-current rounded-full mr-2 animate-pulse"></div>
                        {shipment.status}
                      </div>
                    </div>
                    
                    {/* Info Rows */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-user text-blue text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-grey truncate">
                            {shipment.current_user_name || 'Unassigned'}
                          </div>
                          <div className="text-xs text-grey/70">Assigned User</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-yellow/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="fas fa-clock text-yellow text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-grey">
                            {timeAgo(shipment.status_updated_at)}
                          </div>
                          <div className="text-xs text-grey/70">
                            {new Date(shipment.status_updated_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Enhanced Pagination */}
              {pageCount > 1 && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-6">
                    <div className="text-sm text-grey font-medium text-center sm:text-left">
                      Showing <span className="font-semibold text-blue">{offset + 1}-{Math.min(offset + rowsPerPage, filteredShipments.length)}</span> of <span className="font-semibold text-blue">{filteredShipments.length}</span> inactive shipments
                    </div>
                    
                    <div className="w-full sm:w-auto">
                      <ReactPaginate
                        previousLabel={
                          <div className="flex items-center gap-1 sm:gap-2">
                            <i className="fas fa-chevron-left text-xs"></i>
                            <span className="hidden xs:inline text-sm">Prev</span>
                          </div>
                        }
                        nextLabel={
                          <div className="flex items-center gap-1 sm:gap-2">
                            <span className="hidden xs:inline text-sm">Next</span>
                            <i className="fas fa-chevron-right text-xs"></i>
                          </div>
                        }
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={0}
                        pageRangeDisplayed={1}
                        onPageChange={handlePageChange}
                        containerClassName="flex items-center justify-center gap-1 sm:gap-2"
                        pageClassName="flex-shrink-0"
                        pageLinkClassName="w-8 h-8 sm:w-10 sm:h-10 rounded-xl border border-gray-200 hover:border-blue/30 flex items-center justify-center text-sm font-medium text-grey hover:text-blue hover:bg-blue/5 transition-all duration-200"
                        activeClassName="!bg-gradient-to-br !from-blue !to-blue/90 !border-blue shadow-md !rounded-xl"
                        activeLinkClassName="!text-white font-semibold hover:!text-white !rounded-xl"
                        previousClassName="flex-shrink-0 px-2 sm:px-3 py-2 rounded-xl border border-gray-200 hover:border-blue/30 hover:bg-blue/5 transition-all duration-200"
                        previousLinkClassName="flex items-center gap-1 sm:gap-2 text-sm font-medium text-grey hover:text-blue"
                        nextClassName="flex-shrink-0 px-2 sm:px-3 py-2 rounded-xl border border-gray-200 hover:border-blue/30 hover:bg-blue/5 transition-all duration-200"
                        nextLinkClassName="flex items-center gap-1 sm:gap-2 text-sm font-medium text-grey hover:text-blue"
                        disabledClassName="opacity-50 cursor-not-allowed hover:bg-transparent hover:border-gray-200 hover:text-grey"
                        breakClassName="px-1 py-2 text-grey text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-blue/10 to-blue/20 rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-check-circle text-blue text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue mb-2">All Shipments Active</h3>
              <p className="text-grey text-center max-w-md">
                {selectedYear === "all"
                  ? "Great news! No inactive shipments detected. All shipments have been updated within the last 24 hours."
                  : selectedMonth === "all"
                    ? `No inactive shipments found for ${selectedYear}.`
                    : `No inactive shipments found for ${selectedMonthLabel} ${selectedYear}.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InactiveShipment;
